import React, { useEffect, useState, useRef, Fragment } from 'react'
import PropTypes from 'prop-types'
import { reduxForm, formValues, getFormValues, reset } from 'redux-form'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import BEMHelper from '@ynap/bem-helper'
import Field from '@ynap/field'
import Notification, { Tooltip } from '@ynap/notification'
import { SimpleAsyncButton } from '@ynap/button'
import { RadioButtonGroup } from '@ynap/radio-button'
import { onSubmitFail, submitAndCollateErrorsPromise } from '@ynap/form-utils'
import validate from '../PaymentOptionsForm/validate'
import collateErrors from '../PaymentOptionsForm/collateErrors'
import { trackPaymentSelect } from './analytics'
import { addNewBillingAddress, billingAddressContinue, trackApplePaySetup } from '../analytics'
import track from 'react-tracking'
import { AddressSelectionFormAdapter, mapAddressValues } from '@ynap/address-form-v1'
import { withFormRef, useFormField } from '@ynap/form'
import Totals from '@ynap/totals'
import { getCookie } from '@ynap/redux-cookies-middleware/src/cookieApi'
import DDUAcceptanceForm from '../DDUAcceptanceForm/DDUAcceptanceForm'
import { renderPaymentOptionFactory, StoreCredit } from './renderHelpers'
import { formatDate } from '@ynap/intl-formatters'
import { ERR_INVALID_CARD_TYPE } from '@ynap/credit-card-form'
import { checkoutClearError, submitReduxForm } from 'app/state/cart-state/actions/checkout'
import { ERR_PAYMENT_FAILED, ERR_MISSING_BILLING_ADDRESS } from './ErrorCodes'
import { scrollIntoView } from '@ynap/scroll-utils'
import { usePaymentOptions } from '../../hooks'
import PaymentOptionsModifier from './PaymentOptionsModifier'
import { raygun } from '@ynap/clientside-monitoring'
import { validateKlarnaPhoneNumber } from './utils'
import { setPaymentAddress } from 'app/state/cart-state/actions'
import { cleanAddressEmptyValues } from 'app/utils/formatting'
import { isBillingAddressValid } from 'app/state/cart-state/utils/addressValidationUtils'

/*global ApplePaySession*/
const bem = new BEMHelper('PaymentOptionsForm')

const propTypes = {
    variables: PropTypes.object,
    config: PropTypes.object,
    form: PropTypes.string.isRequired,
    error: PropTypes.string,
    submitting: PropTypes.bool,
    onSubmit: PropTypes.func,
    addAddress: PropTypes.func,
    handleSubmit: PropTypes.func,
    dduInfo: PropTypes.shape({
        dduAcceptance: PropTypes.string,
        deliveryDuty: PropTypes.string,
        shouldAskDDU: PropTypes.bool,
    }),
    messages: PropTypes.shape({
        paymentOptionsForm: PropTypes.shape({
            addCart: PropTypes.func.isRequired,
            payPal: PropTypes.func.isRequired,
            payPalHelp: PropTypes.func.isRequired,
            creditCard: PropTypes.func.isRequired,
            billingAddress: PropTypes.func.isRequired,
            savedCardLabel: PropTypes.func.isRequired,
            submit: PropTypes.func,
            add: PropTypes.func.isRequired,
            PSD2Message: PropTypes.func,
            changePayment: PropTypes.func,
            addPayment: PropTypes.func,
        }).isRequired,
        errors: PropTypes.shape({
            paymentCancelled: PropTypes.func.isRequired,
            paymentFailed: PropTypes.func.isRequired,
        }).isRequired,
    }).isRequired,
    countries: PropTypes.object.isRequired,
    change: PropTypes.func.isRequired,
    totals: PropTypes.shape({
        packaging: PropTypes.string,
        currency: PropTypes.string.isRequired,
        itemTotal: PropTypes.string,
        shipping: PropTypes.string,
        total: PropTypes.string.isRequired,
        promotions: PropTypes.array.isRequired,
        savingsTotal: PropTypes.string,
    }),
    currentShippingAddress: PropTypes.object,
    currentBillingAddress: PropTypes.object,
    initialAddressValues: PropTypes.object,
    paymentOptions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            price: PropTypes.string,
            types: PropTypes.arrayOf(PropTypes.string),
        })
    ).isRequired,
    savedAddresses: PropTypes.array,
    component: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.node,
    isGuestUser: PropTypes.bool,
    newCreditCardId: PropTypes.string.isRequired,
    checkoutError: PropTypes.string,
    locale: PropTypes.object,
    paypalBAEnabled: PropTypes.bool,
    deviceType: PropTypes.string,
    applePayRequest: PropTypes.object,
    configEnabledPaymentMethods: PropTypes.object,
    addressFormClass: PropTypes.string,
    isFetchingProvinces: PropTypes.bool,
    isFetchingAllProvinces: PropTypes.bool,
    isEIP: PropTypes.bool,
    userSummary: PropTypes.object,
}

const defaultProps = {
    isGuestUser: false,
    component: 'form',
    submitting: false,
}

export function PaymentOptionsForm(props) {
    const {
        variables,
        currentBillingAddress,
        blur,
        component: Component,
        config,
        form,
        error,
        submitting,
        handleSubmit,
        onFormSubmit,
        onSubmit,
        addAddress,
        change,
        countries,
        currentShippingAddress = {},
        messages,
        paymentOptions = [],
        savedAddresses = [],
        className,
        children,
        isGuestUser,
        paymentRequired,
        checkoutError,
        tracking,
        locale = {},
        paypalBAEnabled,
        addressFormTouched,
        formRef,
        deviceType,
        addAddressError,
        totals,
        dduInfo: { dduAcceptance, deliveryDuty, shouldAskDDU } = {},
        cvvRequired,
        currentShippingOption,
        dduAcceptanceFormValues,
        fetchProvinces,
        fetchAllProvinces,
        paymentOptionsFormValues = {},
        dispatch,
        applePayRequest,
        configEnabledPaymentMethods,
        addressFormClass,
        checkoutAnalytics,
        isFetchingProvinces,
        isFetchingAllProvinces,
        optimizely,
        isEIP,
        paymentMethodError,
        userSummary = {},
        editAddress,
        _reduxForm,
        initialValues,
    } = props
    let tooltipType = 'error'
    let shippingDateErrorMessage = ''
    const { syncErrors, values } = _reduxForm
    const paymentFormRef = useRef()
    const billingLabelRef = useRef()
    const isMissingBillingAddressError = checkoutError === ERR_MISSING_BILLING_ADDRESS
    const isShippingDateCutOffExceeded = checkoutError === 'INVALID_REQUESTED_DATE'
    const applePayError = checkoutError === 'APPLEPAY_ERROR'
    if (isShippingDateCutOffExceeded) {
        tooltipType = 'info'
        const dateFormatOptions = { weekday: 'long', day: 'numeric', month: 'short', timeZone: 'UTC' }
        const delivery = formatDate(new Date(currentShippingOption.defaultNominatedSlot), dateFormatOptions, locale.id)
        shippingDateErrorMessage = messages.notifications.shippingDateCutOffExceeded.newDate({ description: currentShippingOption.name, delivery })
    }

    const applePayConfig = config?.head?.applepay
    const applePayMerchantID = applePayConfig?.merchantId
    const applePayEnabled = configEnabledPaymentMethods.find((method) => method.name === 'APPLEPAY')?.enabled
    const klarnaEnabled = configEnabledPaymentMethods.find((method) => method.name === 'KLARNA')?.enabled
    const affirmEnabled = configEnabledPaymentMethods.find((method) => method.name === 'AFFIRM')?.enabled

    const getButtonCaption = (option = '') => {
        if (isShippingDateCutOffExceeded) {
            return messages.orderReview.continueAndBuy()
        } else {
            const methodName = option.split(/[_,/]/)[0].toUpperCase()
            const paymentConfig = configEnabledPaymentMethods.find((method) => method.name === methodName)
            const isEnabled = paymentConfig ? paymentConfig.enabled : true
            return messages.orderReview.checkout({ paymentMethodId: isEnabled ? methodName : 'other' })
        }
    }

    const errorWhitelist = [ERR_PAYMENT_FAILED, ERR_INVALID_CARD_TYPE]
    const hasPaypalBA = !!paymentOptions.find((option) => option.billingAgreementToken)
    const paypalFingerprintToken = getCookie('paypal_fingerprint_token')
    const storeCredit = paymentOptions.find((option) => option.id === 'STORE_CREDIT')
    const selectedCardError =
        !!checkoutError &&
        checkoutError != 'CANCELLED' &&
        checkoutError != ERR_INVALID_CARD_TYPE &&
        !isShippingDateCutOffExceeded &&
        !isMissingBillingAddressError &&
        paymentOptionsFormValues.paymentMethodId !== 'CREDIT_CARD'
    const newCardError =
        !!checkoutError &&
        checkoutError != 'CANCELLED' &&
        !isShippingDateCutOffExceeded &&
        !isMissingBillingAddressError &&
        paymentOptionsFormValues.paymentMethodId === 'CREDIT_CARD'
    const klarnaPaymentFailed = checkoutError === 'PAYMENT_FAILED' && /^KLARNA\w*$/.test(paymentOptionsFormValues.paymentMethodId)
    const selectedPaymentId = paymentOptionsFormValues.paymentMethodId
    const [addressIdField, setAddressIdField, clearAddressId] = useFormField('addressId')
    const preselectedOption = paymentOptions.find((option) => option.selected) || {}
    const [buttonCaption, setButtonCaption] = useState(getButtonCaption(preselectedOption.id || preselectedOption.description))
    const errorMessage = error || addAddressError || applePayError ? messages.errors.generic() : shippingDateErrorMessage
    const isDduAcceptanceRequired = dduAcceptanceFormValues?.acceptAdditionalCosts === 'false' && shouldAskDDU
    const [billingAddress, setBillingAddress] = useState(undefined)
    const [billingAddressForm, setBillingAddressForm] = useState(1)
    const [billingAddressError, setBillingAddressError] = useState('')
    const [showChangeAddress, setShowChangeAddress] = useState(false)
    const [addingAddress, setAddingAddress] = useState(false)
    const [loading, setLoading] = useState(false)
    const [applePaySelected, setApplePaySelected] = useState(preselectedOption.id === 'APPLEPAY' && applePayEnabled)
    const [showKlarnaPhoneInvalid, setShowKlarnaPhoneInvalid] = useState(false)
    const [addressSelected, setAddressSelected] = useState('')
    const [addressValidation, setAddressValidation] = useState({})

    useEffect(() => {
        // use previous billing address (if available and valid)
        if (!billingAddress) {
            if (
                initialValues?.billingAddress?.addressId &&
                initialValues?.billingAddress?.addressId !== 'addAddress' &&
                initialValues?.billingAddress?.addressId !== currentShippingAddress?.addressId
            ) {
                const initialAddress = savedAddresses.find((it) => String(it.addressId) === String(initialValues.addressId)) || initialValues?.billingAddress
                setBillingAddress(initialAddress)
            }
        }
    }, [billingAddress, initialValues, savedAddresses, currentShippingAddress, currentBillingAddress])

    useEffect(() => {
        const validateBillingAddress = async () => {
            if (billingAddress?.hasOwnProperty('addressId') && Number(billingAddress.addressId) !== 0) {
                const billingAddressInvalidFields = await isBillingAddressValid(billingAddress, config, messages)
                setAddressValidation({ address: billingAddress.addressId, invalidFields: billingAddressInvalidFields })
            }
        }
        validateBillingAddress()
    }, [billingAddress])

    useEffect(() => {
        if (!isMissingBillingAddressError) return

        raygun.helpers.agent('send', { error: new Error('Missing billing address'), tags: ['checkout', 'missing-billing-addresss'] })
        scrollIntoView(billingLabelRef.current)
        setBillingAddressError(messages.paymentOptionsForm.errors.invalidAddress())
    }, [isMissingBillingAddressError])

    const renderPaymentOption = renderPaymentOptionFactory({
        selectedCardError,
        newCardError,
        klarnaPaymentFailed,
        selectedPaymentId,
        bem,
        messages,
        checkoutError,
        paypalBAEnabled,
        deviceType,
        paypalFingerprintToken,
        hideSaveCard: isGuestUser,
        klarnaEnabled,
        affirmEnabled,
        hasPaypalBA,
        cvvRequired,
        applePayEnabled,
        tracking,
        checkoutAnalytics,
        variables,
    })

    useEffect(() => {
        if (applePayEnabled && applePayMerchantID) {
            ApplePaySession.canMakePaymentsWithActiveCard(applePayMerchantID).then((hasActiveCard) => {
                if (!hasActiveCard) {
                    tracking.trackEvent(trackApplePaySetup(checkoutAnalytics))
                }
            })
        }
    }, [])

    useEffect(() => {
        // only applies to saved cards
        if (cvvRequired !== false && selectedPaymentId?.indexOf('CREDIT_CARD/') !== -1) {
            scrollIntoView(paymentFormRef?.current)
        }
    }, [cvvRequired])

    useEffect(() => {
        if (errorWhitelist.includes(checkoutError)) {
            scrollIntoView(paymentFormRef.current)
        }

        if (checkoutError) {
            setButtonCaption(getButtonCaption(preselectedOption.id))
        }
    }, [checkoutError])

    useEffect(() => {
        if (savedAddresses && countries && Object.keys(countries).length > 0 && !isFetchingProvinces && !isFetchingAllProvinces) {
            const uniqueCountries = savedAddresses.reduce((accumulator, currentValue) => {
                if (!accumulator.includes(currentValue.country)) {
                    accumulator.push(currentValue.country)
                }
                return accumulator
            }, [])

            if (uniqueCountries.length === 1 && !countries[`${uniqueCountries[0]}`]?.provinces) {
                fetchProvinces(uniqueCountries[0])
            }

            if (uniqueCountries.length > 1 && uniqueCountries.find((addressCountry) => !countries[`${addressCountry}`]?.provinces)) {
                fetchAllProvinces()
            }
        }
    }, [])

    useEffect(() => {
        if (preselectedOption.id === 'APPLEPAY' && !applePaySelected) {
            setApplePaySelected(applePayEnabled)
        }
    }, [preselectedOption.id])

    useEffect(() => {
        if (
            typeof billingAddress === 'object' &&
            Object.prototype.hasOwnProperty.call(billingAddress, 'addressId') &&
            values?.paymentMethodId === 'KLARNA' &&
            Number(addressSelected) !== 0
        ) {
            setShowKlarnaPhoneInvalid(!validateKlarnaPhoneNumber(savedAddresses, { addressId: addressSelected }))
        }
    }, [values?.paymentMethodId, addressSelected])

    useEffect(() => {
        let invalidPhoneNotTimer
        if (showKlarnaPhoneInvalid) {
            invalidPhoneNotTimer = setTimeout(() => setShowKlarnaPhoneInvalid(false), 3000)
        }
        return () => {
            clearTimeout(invalidPhoneNotTimer)
        }
    }, [showKlarnaPhoneInvalid])

    const handleSelectChange = (e) => {
        const selected = paymentOptions.find((option) => option.id === e.currentTarget.value)

        setButtonCaption(getButtonCaption(selected.id))
        setApplePaySelected(selected.id === 'APPLEPAY')
        setBillingAddress({})
        setBillingAddressForm((id) => id + 1)
        setShowChangeAddress(false)
        clearAddressId()

        if (checkoutError) {
            dispatch(checkoutClearError())
        }

        dispatch(reset('paymentOptions'))

        return tracking.trackEvent(trackPaymentSelect(selected, checkoutAnalytics))
    }

    const formSubmit = (values, dispatch, props) => {
        const applePaySession = applePaySelected ? new ApplePaySession(applePayConfig?.version || 8, applePayRequest) : null

        if (props.formRef && props.formRef.current && props.formRef.current.hasErrors()) {
            props.formRef.current.scrollToFirstError()
            return null
        }

        // somehow fixes issue where form still has the previous billing address
        values.billingAddress = billingAddress || {}

        if (values.billingAddress.addressId === '' || values.billingAddress.addressId === 'addAddress') {
            delete values.billingAddress.addressId
        }

        if (typeof values.creditCard?.expiryDate === 'string') {
            const [month, year] = values.creditCard.expiryDate.split('/')
            values.creditCard = {
                ...values.creditCard,
                expiryDate: { month, year },
            }
        }

        if (isDduAcceptanceRequired) {
            submitReduxForm('dduAcceptance', dispatch, values)
            return null
        }
        if (values.billingAddress.addressId && typeof values.billingAddress.addressId === 'string') {
            values.billingAddress.addressId = Number(values.billingAddress.addressId)
        }

        if (values.billingAddress.addressId) {
            return submitAndCollateErrorsPromise(
                onFormSubmit({
                    ...{
                        ...values,
                        billingAddress: cleanAddressEmptyValues(values.billingAddress),
                    },
                    applePaySession,
                }),
                values,
                props
            )
        }

        if (addAddress) {
            setAddressIdField({ ...addressIdField, value: billingAddress?.addressId })
            values.billingAddress = {
                addressId: billingAddress?.addressId,
                addressType: billingAddress?.addressType,
                country: billingAddress?.country,
            }
            if (!billingAddress?.addressId) {
                setBillingAddressError(messages.paymentOptionsForm.errors.invalidAddress())
                scrollIntoView(billingLabelRef.current)
                return null
            }
            return submitAndCollateErrorsPromise(
                onFormSubmit({
                    ...{
                        ...values,
                        billingAddress: cleanAddressEmptyValues(values.billingAddress),
                    },
                    applePaySession,
                }),
                values,
                props
            ).catch(() => {
                if (props.formRef && props.formRef.current && props.formRef.current.hasErrors()) {
                    props.formRef.current.scrollToFirstError()
                    return null
                }
            })
        }
    }

    const addAddressWithTransient = addAddress
        ? (address) => {
              return new Promise((resolve) => resolve(window.SF?.getUserSummary()))
                  .then((user) => {
                      address.isTransient = !user
                      return addAddress(address)
                  })
                  .then((result) => {
                      // guest users only have a single billing address
                      if (address.isTransient) {
                          const {
                              payload: { addressId },
                          } = result
                          const newAddress = { ...address, addressId }
                          setAddressIdField({ ...addressIdField, value: addressId })
                          setBillingAddress(newAddress)
                          setShowChangeAddress(false)
                          savedAddresses.push(newAddress)
                      }
                      return result
                  })
          }
        : addAddress

    const handleAddressSubmit = (values) => {
        if (!values.addressId) {
            const mappedValues = mapAddressValues(values, locale)
            setLoading(true)
            addAddressWithTransient &&
                addAddressWithTransient(mappedValues)
                    .then(({ payload: { addressId } }) => {
                        const newAddress = {
                            ...values,
                            addressId,
                        }
                        setAddressIdField({ ...addressIdField, value: addressId })
                        setBillingAddress(newAddress)
                        setShowChangeAddress(false)
                        savedAddresses.push(newAddress)
                        setLoading(false)
                    })
                    .catch(() => {
                        setLoading(false)
                    })
        } else {
            setBillingAddress(values)
        }
    }

    const onChangeAddress = async (addressData) => {
        const addressId = typeof addressData === 'number' || typeof addressData === 'string' ? addressData : addressData?.addressId

        setAddingAddress(addressData === 'add')
        if (addressId === 'changeAddress') {
            setShowChangeAddress(false)
            setBillingAddressForm((id) => id + 1)
        } else if (addressId !== 'add') {
            setAddressSelected(addressData)
            let found = savedAddresses.find((address) => address.addressId === addressId)
            if (found) {
                setBillingAddress(found)
                setShowChangeAddress(false)
            } else if (currentShippingAddress?.addressId === addressId) {
                setBillingAddress(currentShippingAddress)
                setShowChangeAddress(false)
            }

            setBillingAddressError('')

            // update the billing address on the order so that state is updated before we submit the full order
            // helps prevent issues with missing CVV
            if (paymentOptionsFormValues.paymentMethodId?.indexOf('CREDIT_CARD') === 0) {
                new Promise((resolve) => resolve(window.SF?.getUserSummary())).then((user) => {
                    // guest users don't have a saved address to update so we don't need to do anything here
                    if (!user) {
                        return
                    }

                    // save address on change to trigger CVV check if needed
                    dispatch(
                        setPaymentAddress({
                            addressId: +addressId,
                        })
                    )
                })
            }
        }
    }

    const billLabel = messages.paymentOptionsForm?.billingAddressLabel()
    let billingAddresses = [...savedAddresses]

    if (billingAddresses.length === 0 || isGuestUser) {
        billingAddresses = [currentShippingAddress, billingAddress].reduce(
            (acc, address) => (address?.addressId && !acc.find((it) => String(it.addressId) === String(address.addressId)) ? [...acc, address] : acc),
            []
        )
    } else if (billingAddresses?.length > 1 && billingAddress?.addressId && showChangeAddress) {
        billingAddresses = [billingAddress]
    }

    const { activePaymentOptions, canChangePayment, canAddPayment, handleChangePayment, handleAddPayment } = usePaymentOptions(
        paymentOptions,
        configEnabledPaymentMethods,
        optimizely
    )

    return (
        <Component id={form} className={bem(null, { submitting }, className)} onSubmit={handleSubmit(formSubmit)} ref={paymentFormRef}>
            <div className={bem('optionsTitle')}>{messages.paymentOptionsForm.paymentMethods()}</div>

            {storeCredit && <StoreCredit messages={messages} bem={bem} storeCredit={storeCredit} />}
            <>
                {paymentRequired && (
                    <Field component={RadioButtonGroup} className={bem('paymentMethods')} name="paymentMethodId" divided onChange={handleSelectChange}>
                        {activePaymentOptions ? activePaymentOptions.map(renderPaymentOption) : null}
                        <PaymentOptionsModifier
                            canChangePayment={canChangePayment}
                            canAddPayment={canAddPayment}
                            onChangeOptionClick={handleChangePayment}
                            onAddOptionClick={handleAddPayment}
                            messages={messages}
                        />
                    </Field>
                )}
            </>

            <div ref={billingLabelRef} className={bem('billingLabel')}>
                {billLabel}
            </div>

            {paymentMethodError && <Notification type="error" message={messages.notifications.errors({})} />}
            {!!billingAddressError && <Notification type="field-error" message={billingAddressError} className={bem('billingError')} />}
            {showKlarnaPhoneInvalid && (
                <div className={bem('klarnaNotification')}>
                    <Notification type="error" message={messages?.errors?.invalidPhoneNotification()} className={bem('billingError')} />
                </div>
            )}
            {showKlarnaPhoneInvalid}
            <AddressSelectionFormAdapter
                key={`address-form-${billingAddressForm}`}
                name="billingAddress"
                className={bem('addressSelectionForm')}
                config={config}
                childRef={formRef}
                reduxForm={{
                    change,
                    blur,
                    name: 'paymentOptions',
                    syncErrors,
                }}
                addressFormTouched={addressFormTouched}
                addTitle={messages.billingAddressForm.add()}
                defaultAddressLabel={messages.paymentOptionsForm.sameAsShipping()}
                otherAddressLabel={messages.paymentOptionsForm.differentAddress()}
                initialValues={{ ...billingAddress }}
                showChangeAddress={showChangeAddress && savedAddresses?.length > 1 && billingAddress?.addressId}
                addAddress={addAddressWithTransient}
                addresses={billingAddresses}
                defaultAddress={billingAddress}
                messages={messages}
                countries={countries}
                locale={locale}
                tracking={tracking}
                editable={true}
                onSubmit={handleAddressSubmit}
                trackAddNewAddress={() => tracking.trackEvent(addNewBillingAddress(checkoutAnalytics))}
                trackConfirmAddNewAddress={() => tracking.trackEvent(billingAddressContinue(checkoutAnalytics))}
                onChange={onChangeAddress}
                loading={loading}
                split={showChangeAddress || (!showChangeAddress && savedAddresses?.length === 1)}
                addressFormClass={addressFormClass}
                isGuestUser={isGuestUser}
                editAddress={editAddress}
                handleFormCancel={() => {
                    setBillingAddress({})
                    setBillingAddressForm(-2)
                    setAddressSelected('')
                }}
                billingAddressValidation={addressValidation}
            />
            {children}
            {/* only display on the mobile, when review step removed, need update css display block */}

            {!isEIP && config?.head?.flags?.enableReturnFeeInfo?.includes(locale?.country) && (
                <Notification className={bem('returnFeeInfo')} type="info" message={messages.notifications.returnFeeInfo()} />
            )}

            {totals && (
                <Totals
                    className={bem('totals')}
                    {...totals}
                    messages={messages}
                    enablePromotions
                    rewards={totals.rewards || []}
                    taxDutyInfo={totals.dutyType === 'DDU' ? totals.taxDutyInfo : undefined}
                    displaySavingsExplanation={locale?.country !== 'US' && config?.head?.omnibusROWEnabled ? true : false}
                />
            )}
            {onSubmit && (
                <div className={bem('reviewOrderWrapper')}>
                    {shouldAskDDU && (
                        <DDUAcceptanceForm
                            className={bem('dduCheckbox')}
                            messages={messages}
                            deliveryDuty={deliveryDuty}
                            initialValues={{
                                acceptAdditionalCosts: dduAcceptance,
                                checkoutAnalytics,
                            }}
                            checkoutAnalytics={checkoutAnalytics}
                        />
                    )}
                    {deliveryDuty === 'DDU' && dduAcceptance !== 'false' && <div className={bem('dduNotification')}>{messages.orderReview.dduMessage()}</div>}
                    {!addingAddress && (
                        <Tooltip
                            className={bem('buttonWrapper')}
                            type={tooltipType}
                            message={errorMessage}
                            defaultOpen={!submitting && !!errorMessage}
                            key={submitting || errorMessage}
                            childWrapperClassName={'checkout'}
                        >
                            <SimpleAsyncButton
                                copy={buttonCaption}
                                type={applePaySelected ? 'applepay' : 'primary'}
                                className={bem('submit')}
                                loading={submitting}
                                disabled={syncErrors?.billingAddress?.isKlarna || addressValidation?.invalidFields?.length > 0}
                                onClick={() => {
                                    formRef.current?.setTouched(true)
                                }}
                            />
                        </Tooltip>
                    )}
                </div>
            )}
        </Component>
    )
}

PaymentOptionsForm.propTypes = propTypes
PaymentOptionsForm.defaultProps = defaultProps

const PaymentOptionsFormWithConnect = connect((state) => ({
    dduAcceptanceFormValues: getFormValues('dduAcceptance')(state),
    paymentOptionsFormValues: getFormValues('paymentOptions')(state),
}))(PaymentOptionsForm)

export default withFormRef(
    withRouter(
        reduxForm({ form: 'paymentOptions', validate, collateErrors, onSubmitFail })(track()(formValues('billingAddress')(PaymentOptionsFormWithConnect)))
    )
)
